//#region [IMPORT]

import React, { createContext, useEffect, useState } from 'react';

import { onRemoveTaskQueueItem, onTaskQueueInfo, onUpdateTaskQueueInfo, removeListener, requestTask, subscribeTaskQueueInfo, unsubscribeTaskQueueInfo } from 'api/prokey/prokey.websocket';

// === STYLE / MATERIAL UI ===
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
} from '@mui/material';
import './TaskList.css';
import { useTheme } from '@mui/material/styles';
import { Search, UnfoldMoreOutlined, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

const DOCUMENT_COMPARE_METHODS = {
  priority: (a, b) => {
    return (a || 0) - (b || 0);
  },
  queue: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  mode: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  id: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  start_time: (a, b) => {
    const cmp_a = new Date(a).getTime() || 0;
    const cmp_b = new Date(b).getTime() || 0;
    return cmp_a - cmp_b
  },
  status: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  field_count: (a, b) => {
    return (a || 0) - (b || 0);
  },
  user: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  }
};

//-----------------------------------------------------------------
export function TaskList() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const [ tasks , setTasks ] = useState( {} );
  const [ searchInput , setSearchInput ] = useState("")
  const [ sort_header , setSortHeader ] = useState( 'priority' );
  const [ sort_ascend , setSortAscend ] = useState( false );

  const has_data = !!tasks.modules;

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      height   : '100%' ,
      width    : '100%' , 
      padding  : '1rem' ,
      overflow : 'auto' ,
      overflowY : 'hidden',
    },
    container2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      padding  : '0.5rem' ,
      overflow : 'auto' ,
      overflowY : 'hidden',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    title: {
      padding: '1rem', 
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
      gridColumn: 1 / 4,
    },
    table_container: {
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
      height: 'calc( 100% - 4.5rem )',
      overflowY : 'auto',
    },
    table: {

    },
    table_header_cell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    table_cell: {
      borderBottom: 'none',
    },
    search_container: {
      display: 'flex',
      padding: '0.5rem', 
      justifyContent: 'flex-start',
      width: '100%',
      gridColumn: '2 / span 2',
    },
    search_text: {
      width: '100%',
    },
    card: {
      display      : 'grid',
      gap          : '0.5rem',
      'gridTemplateColumns': 'repeat(  1, 1fr )',
      'gridTemplateRows'   : 'max-content 1fr',
      height       : 'calc( 100% - 2rem )' ,
      width        : 'calc( 100% - 2rem )' ,
      maxHeight    : '100%'   ,
      maxWidth     : '100%'   ,
      overflowY    : 'hidden' ,
      marginTop    : '1rem'   ,
      marginLeft   : '1rem'   ,
      marginRight  : '1rem'   ,
      border       : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem'   ,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    table_header_priority: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'priority' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_queue: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'queue' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_mode: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'mode' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_id: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'id' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_start_time: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'start_time' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_status: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'status' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_field_count: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'field_count' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_user: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'user' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    }
  }

  // === ATTRIBUTES

  const
    CONTAINER = {
      sx : _styles.container,
    },
    CONTAINER2 = {
      sx : _styles.container2,
    },
    TITLE = {
      sx       : _styles.title,
      variant  : 'h5',
      color    : theme.palette.primary.main,
      children : [ 'Task Queue Info' ],
    },
    TABLE_CONTAINER = {
      sx      : _styles.table_container,
      variant : 'primary',
    },
    TABLE = {
      stickyHeader : true    ,
      size         : 'small' ,
    },
    SEARCH_CONTAINER = {
      sx: _styles.search_container,
      flexGrow: 1,
    },
    SEARCH_TEXT = {
      sx: _styles.search_text,
      label: 'Search ID...',
      size : 'small', 
      onChange : handleChange,
      name: 'search_task_id_input',
    },
    TABLE_HEADER_PRIORITY = {
      sx            : _styles.table_header_priority,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'priority' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_priority_btn'>{[iconHeader( 'priority' ), 'Priority']}</Box>
    },
    TABLE_HEADER_QUEUE = {
      sx            : _styles.table_header_queue,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'queue' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_queue_btn'>{[iconHeader( 'queue' ), 'Queue']}</Box>
    },
    TABLE_HEADER_MODE = {
      sx            : _styles.table_header_mode,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'mode' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_mode_btn'>{[iconHeader( 'mode' ), 'Mode']}</Box>
    },
    TABLE_HEADER_ID = {
      sx            : _styles.table_header_id,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'id' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_id_btn'>{[iconHeader( 'id' ), 'Task ID']}</Box>
    },
    TABLE_HEADER_START_TIME = {
      sx            : _styles.table_header_start_time,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'start_time' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_start_time_btn'>{[iconHeader( 'start_time' ), 'Start Time']}</Box>
    },
    TABLE_HEADER_STATUS = {
      sx            : _styles.table_header_status,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'status' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_status_btn'>{[iconHeader( 'status' ), 'Status']}</Box>
    },
    TABLE_HEADER_FIELD_COUNT = {
      sx            : _styles.table_header_field_count,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'field_count' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_field_count_btn'>{[iconHeader( 'field_count' ), 'Field Count']}</Box>
    },
    TABLE_HEADER_USER = {
      sx            : _styles.table_header_user,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( 'user' ),
      children      : <Box sx={{display: 'flex'}} name='sort_tbl_header_user_btn'>{[iconHeader( 'user' ), 'Prokey User']}</Box>
    };


  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleQueueCreate(data) {
      //-----------------------------------------------------------------
      setTasks(data);
    }

    onTaskQueueInfo( handleQueueCreate );

    subscribeTaskQueueInfo();

    return () => {
      unsubscribeTaskQueueInfo();
      removeListener( 'TASK-QUEUE-INFO' , handleQueueCreate );
    };
  }, []);

  useEffect( () => {

    //-----------------------------------------------------------------
    function handleQueueUpdate(data) {
      //-----------------------------------------------------------------
      const temp = {...tasks}
      temp.modules[data.module][data.id] = data;
      setTasks( temp );
    }
  
    //-----------------------------------------------------------------
    function handleQueueRemove(data) {
      //-----------------------------------------------------------------
      const temp = {...tasks}
      delete temp.modules[data.module][data.id];
      setTasks( temp );
    }

    onUpdateTaskQueueInfo( handleQueueUpdate );
    onRemoveTaskQueueItem( handleQueueRemove );

    return () => {
      removeListener( 'TASK-QUEUE-INFO-UPDATE', handleQueueUpdate );
      removeListener( 'TASK-QUEUE-INFO-REMOVE', handleQueueRemove );
    };
  }, [tasks] );

  //#endregion

  //#region [green] Events

  //-----------------------------------------------------------------
  function onDoubleClick(event) {
    //-----------------------------------------------------------------
    requestTask( event.currentTarget.id );
  }

  //-----------------------------------------------------------------
  function handleChange(e) {
    //-----------------------------------------------------------------
    e.preventDefault();
    setSearchInput(e.target.value);
  }

  function onClickHeader( name ) {
    //-----------------------------------------------------------------
    if( sort_header === name ) {
      setSortAscend( !sort_ascend );

    } else {
      setSortAscend( true );
      setSortHeader( name );
    }
  }

  //#endregion

  //#region [METHODS]

  //-----------------------------------------------------------------
  function iconHeader( name ) {
    //-----------------------------------------------------------------

    if (sort_header !== name) {
      return <UnfoldMoreOutlined sx={{alignItem: 'center' }} />
    }

    if (sort_ascend) {
      return <KeyboardArrowUp sx={{alignItem: 'center' }} />
    }

    return <KeyboardArrowDown sx={{alignItem: 'center' }} />
  }

  //endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box { ...CONTAINER } >
        <Box { ...CONTAINER2 }>
          <Typography     { ...TITLE           } />
          <Box { ...SEARCH_CONTAINER }>
            <IconButton name='search_task_id_btn'> <Search /> </IconButton>
            <TextField { ...SEARCH_TEXT }/>
          </Box>
        </Box>
        <TableContainer { ...TABLE_CONTAINER }  >
          <Table { ...TABLE }>
            <TableHead >
              <TableRow>
                <TableCell key={0} { ...TABLE_HEADER_PRIORITY       } />
                <TableCell key={1} { ...TABLE_HEADER_QUEUE          } />
                <TableCell key={2} { ...TABLE_HEADER_MODE           } />
                <TableCell key={3} { ...TABLE_HEADER_ID             } />
                <TableCell key={4} { ...TABLE_HEADER_START_TIME     } />
                <TableCell key={5} { ...TABLE_HEADER_STATUS         } />
                <TableCell key={6} { ...TABLE_HEADER_FIELD_COUNT    } />
                <TableCell key={7} { ...TABLE_HEADER_USER           } />
              </TableRow>
            </TableHead>
            <TableBody>{ has_data ? displayData() : displayEmpty() }</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  //-----------------------------------------------------------------
  function displayEmpty() {
    //-----------------------------------------------------------------
    return (
      <TableRow>
        <TableCell colSpan='7' className='allEmptyHere'>
          All empty here...
        </TableCell>
      </TableRow>
    );
  }

  //-----------------------------------------------------------------
  function displayUnauthorized() {
    //-----------------------------------------------------------------
    return (
      <TableRow>
        <TableCell colSpan='7' className='unauthorized'>
          You are not authorized to view tasks...
        </TableCell>
      </TableRow>
    );
  }

  //-----------------------------------------------------------------
  function displayData() {
    //-----------------------------------------------------------------
    const modules      = Object.values(tasks.modules);
    const module_tasks = modules.map( module => Object.values(module) );
    const test        = [].concat( ...module_tasks )
    const tasks_search = test.filter((task) => task.id.toUpperCase().includes(searchInput.toUpperCase()))
    tasks_search.sort((a, b) => DOCUMENT_COMPARE_METHODS[sort_header](a[sort_header], b[sort_header]));
      if( !sort_ascend ) {
        tasks_search.reverse();
      }

    const result = tasks_search.map( task => {
      const row_props = {
        key: task.module + '-' + task.id,
        id: task.id,
        onDoubleClick,
      }
      return (
        <TableRow {...row_props}>
          <TableCell size='large' key={0} sx={_styles.table_cell} >{ task.priority    }</TableCell>
          <TableCell size='large' key={1} sx={_styles.table_cell} >{ task.module      }</TableCell>
          <TableCell size='large' key={2} sx={_styles.table_cell} >{ task.mode        }</TableCell>
          <TableCell size='large' key={3} sx={_styles.table_cell} >{ task.id          }</TableCell>
          <TableCell size='large' key={4} sx={_styles.table_cell} >{ task.start_time  }</TableCell>
          <TableCell size='large' key={5} sx={_styles.table_cell} >{ task.status      }</TableCell>
          <TableCell size='large' key={6} sx={_styles.table_cell} >{ task.field_count }</TableCell>
          <TableCell size='large' key={7} sx={_styles.table_cell} >{ task.user        }</TableCell>
        </TableRow>
      );
    });

    if(result.length === 0) {
      return displayEmpty();
    }

    return result;
  };

  //#endregion

  return display();
}
